import { Fragment, FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";

import { classNames } from "primereact/utils";

import { TooltipWrapper } from "../Components/TooltipWrapper/TooltipWrapper";

import "./ShowTooltipWhenTruncated.scss";

interface IShowTooltipWhenTextTruncatedProps {
  children: ReactNode;
  tooltipContent: string;
  tooltipPosition?: "top" | "bottom" | "left" | "right" | "mouse";
  forceShowTooltip?: boolean;
  lineClamp?: number;
}

export const ShowTooltipWhenTextTruncated: FunctionComponent<IShowTooltipWhenTextTruncatedProps> = (
  props: IShowTooltipWhenTextTruncatedProps,
) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [tooltipDisabled, setTooltipDisabled] = useState(true);

  const isOverflowing = (element: HTMLElement): boolean => {
    // horizontal overflow
    if (element.scrollWidth > element.clientWidth) {
      return true;
    }
    // vertical overflow
    if (element.scrollHeight > element.clientHeight) {
      return true;
    }
    return false;
  };

  const checkTooltipStatus = () => {
    if (props.forceShowTooltip) {
      setTooltipDisabled(false);
      return;
    }

    if (elementRef.current) {
      const rootElement = elementRef.current;
      const firstChild = rootElement.firstElementChild as HTMLElement | null;
      const hasOverflow = isOverflowing(rootElement) || (firstChild && isOverflowing(firstChild));
      setTooltipDisabled(!hasOverflow);
    }
  };

  const debouncedCheckTooltipStatus = debounce(checkTooltipStatus, 200);

  useEffect(() => {
    checkTooltipStatus();

    window.addEventListener("resize", debouncedCheckTooltipStatus);

    return () => {
      window.removeEventListener("resize", debouncedCheckTooltipStatus);
    };
  }, []);

  return (
    <Fragment>
      <TooltipWrapper
        targetRef={elementRef}
        disabled={tooltipDisabled}
        position={props.tooltipPosition}
        content={props.tooltipContent}
      />

      <div
        ref={elementRef}
        onMouseEnter={checkTooltipStatus}
        className={classNames("truncate-container", {
          "is-truncated": !tooltipDisabled,
        })}
        style={props.lineClamp ? { whiteSpace: "normal", lineClamp: props.lineClamp } : {}}
        role="presentation"
        tabIndex={-1}
      >
        {props.children}
      </div>
    </Fragment>
  );
};
